import { ClipboardEvent, KeyboardEvent, MouseEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Check,
  Columns3,
  Eraser,
  Frame,
  Highlighter,
  ImagePlus,
  Italic,
  Link,
  List,
  ListOrdered,
  Maximize2,
  Minimize2,
  Minus,
  Palette,
  Quote,
  Redo2,
  Rows3,
  Strikethrough,
  Table as TableIcon,
  Trash2,
  Type,
  Underline,
  Undo2,
  Upload,
  Video,
  X,
} from "lucide-react";

type InsertPanel = "image" | "video" | "embed" | "link" | "table";
type ImageAlign = "left" | "center" | "right";
type ImageStylePatch = Record<string, string>;
type TableAlign = "left" | "center" | "right";
type TableBorder = "none" | "thin" | "thick" | "dashed";
type CellPadding = "small" | "medium" | "large";

/** 允許嵌入的網域白名單，避免使用者貼上任意網址造成惡意內容被嵌入（例如釣魚頁面）。 */
const ALLOWED_EMBED_HOSTS = ["www.youtube.com", "player.vimeo.com", "vimeo.com", "www.google.com", "docs.google.com", "open.spotify.com"];

function youtubeEmbedUrl(value: string) {
  const trimmed = value.trim();
  const matched = trimmed.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return matched ? `https://www.youtube.com/embed/${matched[1]}` : "";
}

function isImageUrl(value: string) {
  return /^https?:\/\/.+\.(jpe?g|png|webp|gif|svg)(\?.*)?$/i.test(value.trim());
}

function isSafeUrl(value: string) {
  return /^(https?:|mailto:|tel:|\/)/i.test(value.trim());
}

/** 有些嵌入服務（例如 Google 地圖的「嵌入地圖」）給的是整段 <iframe> 程式碼而不是單純網址，這裡把 src 取出來。 */
function extractIframeSrc(value: string) {
  const trimmed = value.trim();
  if (/^<iframe/i.test(trimmed)) {
    const doc = new DOMParser().parseFromString(trimmed, "text/html");
    return doc.querySelector("iframe")?.getAttribute("src")?.trim() || "";
  }
  return trimmed;
}

function isAllowedIframeSrc(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && ALLOWED_EMBED_HOSTS.includes(url.hostname);
  } catch {
    return false;
  }
}

/** 把 Vimeo／Spotify 常見的「分享連結」轉成可以嵌入的網址，減少使用者要自己去找「嵌入專用網址」的麻煩。 */
function normalizeEmbedUrl(rawValue: string) {
  const value = extractIframeSrc(rawValue);
  try {
    const url = new URL(value);
    if (url.hostname === "vimeo.com") {
      const matched = url.pathname.match(/^\/(\d+)/);
      if (matched) return `https://player.vimeo.com/video/${matched[1]}`;
    }
    if (url.hostname === "open.spotify.com" && !url.pathname.startsWith("/embed/")) {
      return `https://open.spotify.com/embed${url.pathname}`;
    }
    return value;
  } catch {
    return value;
  }
}

function sanitizeEditorHtml(html: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const allowedTags = new Set(["A", "B", "BLOCKQUOTE", "BR", "DIV", "EM", "FIGCAPTION", "FIGURE", "FONT", "H1", "H2", "H3", "H4", "H5", "H6", "HR", "I", "IFRAME", "IMG", "LI", "OL", "P", "S", "SPAN", "STRIKE", "STRONG", "TABLE", "TBODY", "TD", "TH", "THEAD", "TR", "U", "UL"]);
  const allowedAttrs = new Set(["href", "src", "alt", "title", "target", "rel", "style", "color", "size", "width", "height", "allow", "allowfullscreen", "frameborder", "loading", "colspan", "rowspan", "cellpadding", "cellspacing", "border", "data-editor-table"]);

  doc.body.querySelectorAll("*").forEach((node) => {
    if (!allowedTags.has(node.tagName)) {
      node.replaceWith(...Array.from(node.childNodes));
      return;
    }

    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (!allowedAttrs.has(name)) {
        node.removeAttribute(attr.name);
        return;
      }
      if ((name === "src" || name === "href") && !isSafeUrl(attr.value)) {
        node.removeAttribute(attr.name);
      }
      if (name === "style") {
        const safeStyle = attr.value
          .split(";")
          .map((item) => item.trim())
          .filter((item) => /^(color|font-size|font-weight|text-align|background-color|width|max-width|min-width|height|display|overflow-x|margin|margin-left|margin-right|float|border(-\w+)?|border-collapse|padding|padding(-\w+)?|vertical-align|caption-side)\s*:/i.test(item))
          .join("; ");
        if (safeStyle) node.setAttribute("style", safeStyle);
        else node.removeAttribute(attr.name);
      }
    });

    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noreferrer");
    }
    if (node.tagName === "IMG") {
      node.setAttribute("loading", "lazy");
    }
    if (node.tagName === "IFRAME") {
      const src = node.getAttribute("src") || "";
      if (!isAllowedIframeSrc(src)) {
        node.remove();
        return;
      }
      node.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share");
      node.setAttribute("allowfullscreen", "true");
      node.setAttribute("frameborder", "0");
    }
  });

  return doc.body.innerHTML;
}

function imageAlignFromStyle(image: HTMLImageElement): ImageAlign {
  if (image.style.marginLeft === "auto" && image.style.marginRight === "auto") return "center";
  if (image.style.marginLeft === "auto") return "right";
  return "left";
}

const TABLE_CELL_STYLE = "border:1px solid #cfd8d2;padding:8px;vertical-align:top;";
const TABLE_HEADER_CELL_STYLE = TABLE_CELL_STYLE + "background:#f0f3f1;font-weight:bold;";
const TABLE_PADDING: Record<CellPadding, string> = { small: "6px", medium: "10px", large: "16px" };
const TABLE_BORDER: Record<TableBorder, { width: string; style: string; color: string }> = {
  none: { width: "0", style: "none", color: "transparent" },
  thin: { width: "1px", style: "solid", color: "#cfd8d2" },
  thick: { width: "2px", style: "solid", color: "#9aa8a0" },
  dashed: { width: "1px", style: "dashed", color: "#9aa8a0" },
};

function setInlineStyles(element: HTMLElement, styles: Record<string, string>) {
  Object.entries(styles).forEach(([property, propertyValue]) => {
    element.style.setProperty(property, propertyValue);
  });
}

function tableAlignFromStyle(table: HTMLTableElement): TableAlign {
  if (table.style.marginLeft === "auto" && table.style.marginRight === "auto") return "center";
  if (table.style.marginLeft === "auto") return "right";
  return "left";
}

function tableWidthFromStyle(table: HTMLTableElement) {
  const matched = (table.style.width || "").match(/^(\d+)%$/);
  return matched ? matched[1] : "100";
}

function tableBorderFromStyle(cell: HTMLTableCellElement): TableBorder {
  if (cell.style.borderStyle === "none" || cell.style.borderWidth === "0px") return "none";
  if (cell.style.borderStyle === "dashed") return "dashed";
  if (parseInt(cell.style.borderWidth || "1", 10) >= 2) return "thick";
  return "thin";
}

function tablePaddingFromStyle(cell: HTMLTableCellElement): CellPadding {
  const padding = parseInt(cell.style.padding || "10", 10);
  if (padding <= 6) return "small";
  if (padding >= 16) return "large";
  return "medium";
}

function ensureTableWrapper(table: HTMLTableElement) {
  const parent = table.parentElement;
  if (parent?.dataset.editorTable === "wrapper") return parent;
  const wrapper = document.createElement("div");
  wrapper.dataset.editorTable = "wrapper";
  wrapper.setAttribute("style", "overflow-x:auto;width:100%;");
  table.parentNode?.insertBefore(wrapper, table);
  wrapper.appendChild(table);
  return wrapper;
}

/** 表格內容用 inline style（而非 CSS class）撰寫，這樣同一份 HTML 拿去當電子報寄出時，Email 客戶端也能正確顯示邊框與間距。 */
function buildTableHtml(rows: number, columns: number) {
  let html = '<div data-editor-table="wrapper" style="overflow-x:auto;width:100%;"><table style="width:100%;border-collapse:collapse;margin:12px 0;">';
  for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
    html += "<tr>";
    for (let columnIndex = 0; columnIndex < columns; columnIndex++) {
      const style = rowIndex === 0 ? TABLE_HEADER_CELL_STYLE : TABLE_CELL_STYLE;
      html += `<td style="${style}">${rowIndex === 0 ? `欄位 ${columnIndex + 1}` : "&nbsp;"}</td>`;
    }
    html += "</tr>";
  }
  html += "</table></div><p><br></p>";
  return html;
}

/** 顏色選擇按鈕：把原生 input[type=color] 縮到極小並蓋在整個按鈕上（維持好按的點擊範圍），視覺上只露出一個小色塊，避免原生色塊在部分瀏覽器上顯示過大。 */
function ColorSwatchButton({
  icon,
  title,
  value,
  onMouseDown,
  onPick,
}: {
  icon: ReactNode;
  title: string;
  value: string;
  onMouseDown: () => void;
  onPick: (value: string) => void;
}) {
  return (
    <label
      className="relative inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md px-2 text-[#315746] hover:bg-[#e9f4ec]"
      title={title}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {icon}
      <span className="h-4 w-4 shrink-0 rounded border border-[#cfd8d2]" style={{ backgroundColor: value }} />
      <input
        aria-label={title}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        type="color"
        value={value}
        onChange={(event) => onPick(event.target.value)}
        onMouseDown={onMouseDown}
      />
    </label>
  );
}

export function AdminRichTextEditor({
  value,
  onChange,
  onUploadImage,
  imageAlt = "內容圖片",
}: {
  value: string;
  onChange: (value: string) => void;
  onUploadImage: (file: File) => Promise<string>;
  imageAlt?: string;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const selectedImageRef = useRef<HTMLImageElement | null>(null);
  const selectedCellRef = useRef<HTMLTableCellElement | null>(null);
  const changeTimerRef = useRef<number | null>(null);
  const [editorUploading, setEditorUploading] = useState(false);
  const [insertPanel, setInsertPanel] = useState<InsertPanel | null>(null);
  const [urlValue, setUrlValue] = useState("");
  const [insertError, setInsertError] = useState("");
  /** 字體大小／段落標題是原生 <select>，選過一次後瀏覽器會一直顯示上次選的項目；
   *  如果使用者想對「另一段新選取的文字」再套用同一個選項，原生 select 不會觸發 onChange（值沒變），
   *  等於按了沒反應。用這個計數器當 key 強制每次套用後重新掛載回預設的「請選擇」提示字，行為才會一致。 */
  const [selectResetKey, setSelectResetKey] = useState(0);
  const [tableRows, setTableRows] = useState("3");
  const [tableColumns, setTableColumns] = useState("3");
  const [selectedImageWidth, setSelectedImageWidth] = useState("100");
  const [selectedImageAlign, setSelectedImageAlign] = useState<ImageAlign>("left");
  const [selectedImageAlt, setSelectedImageAlt] = useState("");
  const [selectedImageCaption, setSelectedImageCaption] = useState("");
  const [selectedImageLink, setSelectedImageLink] = useState("");
  const [tableWidth, setTableWidth] = useState("100");
  const [tableAlign, setTableAlign] = useState<TableAlign>("left");
  const [tableBorder, setTableBorder] = useState<TableBorder>("thin");
  const [tablePadding, setTablePadding] = useState<CellPadding>("medium");
  const [tableCellAlign, setTableCellAlign] = useState<ImageAlign>("left");
  const [tableCellBg, setTableCellBg] = useState("#ffffff");
  const [imageToolbar, setImageToolbar] = useState<{ left: number; top: number } | null>(null);
  const [tableToolbar, setTableToolbar] = useState<{ left: number; top: number } | null>(null);
  const [foreColor, setForeColor] = useState("#1B3D2A");
  const [highlightColor, setHighlightColor] = useState("#FFF3B0");
  const [isFullscreen, setIsFullscreen] = useState(false);
  /**
   * 全螢幕切換時，編輯器內容（含 contentEditable 裡目前輸入到一半、尚未同步回 value 的文字）
   * 不能被重新掛載，否則畫面會整個變空白。做法是準備一個「永遠存在」的 DOM 容器，React 永遠
   * 只 portal 進這一個容器裡（reconcile 時樹狀結構完全沒變，不會 unmount/remount），
   * 全螢幕與否只是用原生 DOM API 把這個容器整個搬到 <body> 或搬回原本卡片內，
   * 瀏覽器搬移既有節點時會保留其中的內容、游標、捲動位置等狀態。
   */
  const [portalContainer] = useState(() => {
    const element = document.createElement("div");
    element.className = "contents";
    return element;
  });
  const placeholderRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    return () => {
      portalContainer.remove();
    };
  }, [portalContainer]);

  useLayoutEffect(() => {
    const target = isFullscreen ? document.body : placeholderRef.current;
    if (target && portalContainer.parentElement !== target) {
      target.appendChild(portalContainer);
    }
  }, [isFullscreen, portalContainer]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      if (editorRef.current.contains(document.activeElement) || selectedImageRef.current || selectedCellRef.current) {
        return;
      }
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (changeTimerRef.current) window.clearTimeout(changeTimerRef.current);
    };
  }, []);

  /** 全螢幕模式下鎖住背景頁面捲動，並支援按 Esc 離開，行為與一般全螢幕編輯工具一致。 */
  useEffect(() => {
    if (!isFullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") setIsFullscreen(false);
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreen]);

  function saveSelection() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current?.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  }

  function restoreSelection() {
    const selection = window.getSelection();
    const range = savedRangeRef.current;
    if (!selection || !range) return;
    selection.removeAllRanges();
    selection.addRange(range);
  }

  function emitChange(immediate = false) {
    const html = sanitizeEditorHtml(editorRef.current?.innerHTML || "");
    if (immediate) {
      if (changeTimerRef.current) window.clearTimeout(changeTimerRef.current);
      onChange(html);
      return;
    }
    if (changeTimerRef.current) window.clearTimeout(changeTimerRef.current);
    changeTimerRef.current = window.setTimeout(() => onChange(html), 120);
  }

  function toolbarMouseDown(event: MouseEvent) {
    event.preventDefault();
    saveSelection();
  }

  function imageToolbarMouseDown(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  function command(name: string, argument?: string) {
    restoreSelection();
    editorRef.current?.focus();
    document.execCommand(name, false, argument);
    saveSelection();
    emitChange(true);
  }

  function applyForeColor(value: string) {
    setForeColor(value);
    command("foreColor", value);
  }

  /** 螢光筆／背景標色：Firefox 用 hiliteColor，其餘瀏覽器（含 Chrome）較穩定的是 backColor，先偵測支援度再決定。 */
  function applyHighlightColor(value: string) {
    setHighlightColor(value);
    restoreSelection();
    editorRef.current?.focus();
    const supportsHiliteColor = typeof document.queryCommandSupported === "function" && document.queryCommandSupported("hiliteColor");
    document.execCommand(supportsHiliteColor ? "hiliteColor" : "backColor", false, value);
    saveSelection();
    emitChange(true);
  }

  function insertHtml(html: string) {
    restoreSelection();
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, html);
    saveSelection();
    emitChange(true);
  }

  function insertImage(url: string, alt = imageAlt) {
    insertHtml(`<img src="${url}" alt="${alt}" style="display: block; width: 100%; max-width: 100%; height: auto; margin-left: auto; margin-right: auto;" />`);
  }

  function openInsertPanel(panel: InsertPanel) {
    saveSelection();
    setUrlValue("");
    setInsertError("");
    setInsertPanel((current) => (current === panel ? null : panel));
  }

  /** 之前這裡驗證失敗時什麼都不會發生（沒有錯誤訊息也不會關閉面板），使用者會以為「插入」按鈕沒反應，
   *  現在改成每個分支驗證失敗都設定明確的錯誤訊息，顯示在輸入框下方。 */
  function submitUrlInsert() {
    const url = urlValue.trim();
    if (!url) {
      setInsertError("請先輸入網址。");
      return;
    }
    if (insertPanel === "image") {
      if (!isSafeUrl(url)) {
        setInsertError("網址格式不正確，請確認是以 https://、http://、mailto: 或 tel: 開頭的網址。");
        return;
      }
      insertImage(url);
      setUrlValue("");
      setInsertError("");
      setInsertPanel(null);
      return;
    }
    if (insertPanel === "video") {
      const embedUrl = youtubeEmbedUrl(url);
      if (!embedUrl) {
        setInsertError("無法辨識這個網址，請貼上完整的 YouTube 影片網址。");
        return;
      }
      insertHtml(`<iframe src="${embedUrl}" title="YouTube video" loading="lazy"></iframe>`);
      setUrlValue("");
      setInsertError("");
      setInsertPanel(null);
      return;
    }
    if (insertPanel === "embed") {
      const embedSrc = normalizeEmbedUrl(url);
      if (!isAllowedIframeSrc(embedSrc)) {
        setInsertError("僅支援 Google 地圖、Google 表單、Vimeo、Spotify 提供的「嵌入」網址或程式碼，請確認來源網址。");
        return;
      }
      insertHtml(`<iframe src="${embedSrc}" title="嵌入內容" loading="lazy"></iframe>`);
      setUrlValue("");
      setInsertError("");
      setInsertPanel(null);
      return;
    }
    if (insertPanel === "link") {
      if (!isSafeUrl(url)) {
        setInsertError("網址格式不正確，請確認是以 https://、http://、mailto: 或 tel: 開頭的網址。");
        return;
      }
      command("createLink", url);
      setUrlValue("");
      setInsertError("");
      setInsertPanel(null);
    }
  }

  function submitTableInsert() {
    const rows = Math.min(20, Math.max(1, parseInt(tableRows, 10) || 3));
    const columns = Math.min(10, Math.max(1, parseInt(tableColumns, 10) || 3));
    insertHtml(buildTableHtml(rows, columns));
    setInsertPanel(null);
  }

  function submitTableInsertOnEnter(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      submitTableInsert();
    }
  }

  function updateImageToolbar(image: HTMLImageElement | null) {
    if (!image || !rootRef.current) {
      setImageToolbar(null);
      return;
    }
    const imageRect = image.getBoundingClientRect();
    const rootRect = rootRef.current.getBoundingClientRect();
    const left = Math.max(12, Math.min(imageRect.left - rootRect.left, rootRect.width - 380));
    const top = Math.max(54, imageRect.top - rootRect.top - 52);
    setImageToolbar({ left, top });
  }

  function selectImage(image: HTMLImageElement | null) {
    selectedImageRef.current = image;
    if (!image) {
      setImageToolbar(null);
      return;
    }
    const parsedWidth = parseInt(image.style.width || image.getAttribute("width") || "100", 10);
    setSelectedImageWidth(Number.isFinite(parsedWidth) ? String(parsedWidth) : "100");
    setSelectedImageAlign(imageAlignFromStyle(image));
    setSelectedImageAlt(image.getAttribute("alt") || "");
    setSelectedImageCaption(image.closest("figure")?.querySelector("figcaption")?.textContent || "");
    setSelectedImageLink(image.closest("a")?.getAttribute("href") || "");
    updateImageToolbar(image);
  }

  function applySelectedImageStyle(nextStyle: ImageStylePatch) {
    const image = selectedImageRef.current;
    if (!image) return;
    setInlineStyles(image, nextStyle);
    image.removeAttribute("width");
    image.removeAttribute("height");
    updateImageToolbar(image);
    emitChange(true);
    selectImage(image);
  }

  function resizeSelectedImage(width: string) {
    setSelectedImageWidth(width);
    applySelectedImageStyle({ width: `${width}%`, "max-width": "100%", height: "auto" });
  }

  function alignSelectedImage(position: ImageAlign) {
    setSelectedImageAlign(position);
    const baseStyle: ImageStylePatch = { display: "block", float: "none" };
    if (position === "left") applySelectedImageStyle({ ...baseStyle, "margin-left": "0", "margin-right": "auto" });
    if (position === "center") applySelectedImageStyle({ ...baseStyle, "margin-left": "auto", "margin-right": "auto" });
    if (position === "right") applySelectedImageStyle({ ...baseStyle, "margin-left": "auto", "margin-right": "0" });
  }

  function updateSelectedImageAlt(value: string) {
    setSelectedImageAlt(value);
    const image = selectedImageRef.current;
    if (!image) return;
    image.setAttribute("alt", value);
    emitChange(true);
  }

  function ensureImageFigure(image: HTMLImageElement) {
    const existingFigure = image.closest("figure");
    if (existingFigure) return existingFigure;
    const figure = document.createElement("figure");
    figure.setAttribute("style", "margin:12px 0;");
    const parent = image.parentElement;
    const wrapper = parent?.tagName === "A" ? parent : image;
    wrapper.parentNode?.insertBefore(figure, wrapper);
    figure.appendChild(wrapper);
    return figure;
  }

  function updateSelectedImageCaption(value: string) {
    setSelectedImageCaption(value);
    const image = selectedImageRef.current;
    if (!image) return;
    const trimmed = value.trim();
    const figure = ensureImageFigure(image);
    const existing = figure.querySelector("figcaption");
    if (!trimmed) {
      existing?.remove();
      emitChange(true);
      selectImage(image);
      return;
    }
    const caption = existing || document.createElement("figcaption");
    caption.textContent = trimmed;
    caption.setAttribute("style", "color:#59665f;font-size:0.9em;text-align:center;margin-top:6px;");
    if (!existing) figure.appendChild(caption);
    emitChange(true);
    selectImage(image);
  }

  function updateSelectedImageLink(value: string) {
    setSelectedImageLink(value);
    const image = selectedImageRef.current;
    if (!image) return;
    const trimmed = value.trim();
    const currentLink = image.closest("a");
    if (!trimmed) {
      if (currentLink) currentLink.replaceWith(image);
      emitChange(true);
      selectImage(image);
      return;
    }
    if (!isSafeUrl(trimmed)) return;
    const link = currentLink || document.createElement("a");
    link.setAttribute("href", trimmed);
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noreferrer");
    if (!currentLink) {
      image.parentNode?.insertBefore(link, image);
      link.appendChild(image);
    }
    emitChange(true);
    selectImage(image);
  }

  function deleteSelectedImage() {
    const image = selectedImageRef.current;
    if (!image) return;
    const figure = image.closest("figure");
    const link = image.closest("a");
    if (figure) figure.remove();
    else if (link) link.remove();
    else image.remove();
    selectedImageRef.current = null;
    setImageToolbar(null);
    emitChange(true);
  }

  function updateTableToolbar(cell: HTMLTableCellElement | null) {
    if (!cell || !rootRef.current) {
      setTableToolbar(null);
      return;
    }
    const cellRect = cell.getBoundingClientRect();
    const rootRect = rootRef.current.getBoundingClientRect();
    const left = Math.max(12, Math.min(cellRect.left - rootRect.left, rootRect.width - 480));
    const top = Math.max(54, cellRect.top - rootRect.top - 52);
    setTableToolbar({ left, top });
  }

  function selectCell(cell: HTMLTableCellElement | null) {
    selectedCellRef.current = cell;
    if (cell) syncTableControls(cell);
    updateTableToolbar(cell);
  }

  function syncTableControls(cell: HTMLTableCellElement) {
    const table = cell.closest("table");
    if (!table) return;
    ensureTableWrapper(table);
    setTableWidth(tableWidthFromStyle(table));
    setTableAlign(tableAlignFromStyle(table));
    setTableBorder(tableBorderFromStyle(cell));
    setTablePadding(tablePaddingFromStyle(cell));
    setTableCellAlign((cell.style.textAlign as ImageAlign) || "left");
    setTableCellBg(cell.style.backgroundColor || "#ffffff");
  }

  function selectedTable() {
    return selectedCellRef.current?.closest("table") ?? null;
  }

  function applySelectedTableStyle(action: (cell: HTMLTableCellElement, table: HTMLTableElement) => void) {
    const cell = selectedCellRef.current;
    const table = selectedTable();
    if (!cell || !table) return;
    ensureTableWrapper(table);
    action(cell, table);
    emitChange(true);
    syncTableControls(cell);
    updateTableToolbar(cell);
  }

  function resizeSelectedTable(width: string) {
    setTableWidth(width);
    applySelectedTableStyle((_cell, table) => {
      table.style.setProperty("width", `${width}%`);
      table.style.setProperty("max-width", "100%");
      table.style.setProperty("border-collapse", "collapse");
    });
  }

  function alignSelectedTable(position: TableAlign) {
    setTableAlign(position);
    applySelectedTableStyle((_cell, table) => {
      table.style.setProperty("display", "table");
      if (position === "left") table.style.setProperty("margin", "12px auto 12px 0");
      if (position === "center") table.style.setProperty("margin", "12px auto");
      if (position === "right") table.style.setProperty("margin", "12px 0 12px auto");
    });
  }

  function applyTableBorder(kind: TableBorder) {
    setTableBorder(kind);
    applySelectedTableStyle((_cell, table) => {
      const border = TABLE_BORDER[kind];
      table.style.setProperty("border-collapse", "collapse");
      table.querySelectorAll("td,th").forEach((tableCell) => {
        const element = tableCell as HTMLElement;
        element.style.setProperty("border-width", border.width);
        element.style.setProperty("border-style", border.style);
        element.style.setProperty("border-color", border.color);
      });
    });
  }

  function applyTablePadding(size: CellPadding) {
    setTablePadding(size);
    applySelectedTableStyle((_cell, table) => {
      table.querySelectorAll("td,th").forEach((tableCell) => {
        (tableCell as HTMLElement).style.setProperty("padding", TABLE_PADDING[size]);
      });
    });
  }

  function applySelectedCellTextAlign(position: ImageAlign) {
    setTableCellAlign(position);
    applySelectedTableStyle((cell) => {
      cell.style.setProperty("text-align", position);
    });
  }

  function applySelectedCellBackground(value: string) {
    setTableCellBg(value);
    applySelectedTableStyle((cell) => {
      cell.style.setProperty("background-color", value);
    });
  }

  function toggleHeaderRow() {
    applySelectedTableStyle((_cell, table) => {
      const firstRow = table.querySelector("tr");
      if (!firstRow) return;
      const firstCell = firstRow.querySelector("td,th") as HTMLElement | null;
      const isHeader = firstCell?.style.fontWeight === "bold";
      firstRow.querySelectorAll("td,th").forEach((tableCell) => {
        const element = tableCell as HTMLElement;
        element.style.setProperty("font-weight", isHeader ? "normal" : "bold");
        element.style.setProperty("background-color", isHeader ? "#ffffff" : "#f0f3f1");
      });
    });
  }

  /** 針對目前選取儲存格所在的表格做結構調整（新增/刪除列或欄），結束後套用變更並隱藏工具列。 */
  function withTableUpdate(action: (cell: HTMLTableCellElement, row: HTMLTableRowElement, table: HTMLTableElement) => void) {
    const cell = selectedCellRef.current;
    const row = cell?.closest("tr") ?? null;
    const table = cell?.closest("table") ?? null;
    if (!cell || !row || !table) return;
    ensureTableWrapper(table);
    action(cell, row, table);
    emitChange(true);
    selectCell(null);
  }

  function buildTableCell(referenceStyle: string, isHeaderRow: boolean) {
    const cell = document.createElement("td");
    cell.setAttribute("style", isHeaderRow ? TABLE_HEADER_CELL_STYLE : referenceStyle || TABLE_CELL_STYLE);
    cell.innerHTML = "&nbsp;";
    return cell;
  }

  function insertTableRow(position: "above" | "below") {
    withTableUpdate((_cell, row) => {
      const isFirstRow = row.previousElementSibling === null;
      const newRow = document.createElement("tr");
      Array.from(row.children).forEach((existingCell) => {
        newRow.appendChild(buildTableCell((existingCell as HTMLTableCellElement).getAttribute("style") || "", false));
      });
      row.parentElement?.insertBefore(newRow, position === "above" ? row : row.nextElementSibling);
      // 如果是在標題列（第一列）上方插入新列，標題樣式要跟著「現在排在最上面的那一列」走，
      // 所以是新插入的 newRow 變成標題、原本的標題列 row 被往下推變成一般列（先前這裡兩行寫反了）。
      if (position === "above" && isFirstRow) {
        newRow.querySelectorAll("td").forEach((cell) => cell.setAttribute("style", TABLE_HEADER_CELL_STYLE));
        row.querySelectorAll("td").forEach((cell) => cell.setAttribute("style", TABLE_CELL_STYLE));
      }
    });
  }

  function insertTableColumn(position: "left" | "right") {
    withTableUpdate((cell, row, table) => {
      const columnIndex = Array.from(row.children).indexOf(cell);
      table.querySelectorAll("tr").forEach((tableRow) => {
        const referenceCell = tableRow.children[columnIndex] as HTMLTableCellElement | undefined;
        if (!referenceCell) return;
        const isHeaderRow = tableRow === table.querySelector("tr");
        const newCell = buildTableCell(referenceCell.getAttribute("style") || "", isHeaderRow);
        tableRow.insertBefore(newCell, position === "left" ? referenceCell : referenceCell.nextElementSibling);
      });
    });
  }

  function deleteTableRow() {
    withTableUpdate((_cell, row, table) => {
      if (table.querySelectorAll("tr").length <= 1) {
        table.remove();
        return;
      }
      const wasHeaderRow = row === table.querySelector("tr");
      row.remove();
      // 如果刪掉的剛好是標題列，讓遞補上來變成第一列的那一列改套用標題樣式，避免表格從此沒有標題列。
      if (wasHeaderRow) {
        table.querySelector("tr")?.querySelectorAll("td").forEach((cell) => cell.setAttribute("style", TABLE_HEADER_CELL_STYLE));
      }
    });
  }

  function deleteTableColumn() {
    withTableUpdate((cell, row, table) => {
      const columnIndex = Array.from(row.children).indexOf(cell);
      if (row.children.length <= 1) {
        table.remove();
      } else {
        table.querySelectorAll("tr").forEach((tableRow) => {
          tableRow.children[columnIndex]?.remove();
        });
      }
    });
  }

  function deleteTable() {
    withTableUpdate((_cell, _row, table) => {
      table.remove();
    });
  }

  async function uploadEditorImage(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setEditorUploading(true);
    try {
      const url = await onUploadImage(file);
      insertImage(url, file.name);
    } finally {
      setEditorUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  }

  async function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    const imageFile = Array.from(event.clipboardData.files).find((file) => file.type.startsWith("image/"));
    if (imageFile) {
      event.preventDefault();
      await uploadEditorImage({ 0: imageFile, length: 1, item: () => imageFile } as unknown as FileList);
      return;
    }

    const html = event.clipboardData.getData("text/html");
    if (html) {
      event.preventDefault();
      insertHtml(sanitizeEditorHtml(html));
      return;
    }

    const text = event.clipboardData.getData("text/plain").trim();
    if (!text) return;
    const embedUrl = youtubeEmbedUrl(text);
    if (embedUrl) {
      event.preventDefault();
      insertHtml(`<iframe src="${embedUrl}" title="YouTube video" loading="lazy"></iframe>`);
      return;
    }
    if (isImageUrl(text)) {
      event.preventDefault();
      insertImage(text);
    }
  }

  const imageButtonClass = (active: boolean) =>
    `inline-flex h-8 items-center justify-center rounded-md border px-2.5 text-xs font-bold transition ${
      active ? "border-[#315746] bg-[#315746] text-white" : "border-[#cfd8d2] bg-white text-[#315746] hover:bg-[#f0f5f2]"
    }`;

  const editorBody = (
    <div
      ref={rootRef}
      className={
        isFullscreen
          ? "fixed inset-0 z-[100] flex flex-col overflow-hidden bg-white"
          : "relative overflow-hidden rounded-md border border-[#cfd8d2] bg-white"
      }
    >
      <div
        className={isFullscreen ? "flex flex-1 flex-col overflow-y-auto" : "overflow-visible"}
        onScroll={() => {
          updateImageToolbar(selectedImageRef.current);
          updateTableToolbar(selectedCellRef.current);
        }}
      >
      {/* 工具列與插入面板包在同一個 sticky 區塊裡，往下捲動瀏覽內容時會一起固定在編輯器頂端，
          不會被外層「回列表／預覽／儲存」那條固定列擋住，面板高度變化時瀏覽器會自動重新計算，不需要手動抓像素。 */}
      <div className="sticky top-0 z-20 bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-[#edf0ee] bg-[#f7f9f8] p-2" onMouseDown={toolbarMouseDown}>
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="復原" type="button" onClick={() => command("undo")}><Undo2 size={16} /></button>
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="取消復原" type="button" onClick={() => command("redo")}><Redo2 size={16} /></button>
        <span className="mx-1 h-6 w-px bg-[#dfe6e1]" aria-hidden="true" />
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="粗體" type="button" onClick={() => command("bold")}><Bold size={16} /></button>
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="斜體" type="button" onClick={() => command("italic")}><Italic size={16} /></button>
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="底線" type="button" onClick={() => command("underline")}><Underline size={16} /></button>
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="刪除線" type="button" onClick={() => command("strikeThrough")}><Strikethrough size={16} /></button>
        <span className="mx-1 h-6 w-px bg-[#dfe6e1]" aria-hidden="true" />
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="項目清單" type="button" onClick={() => command("insertUnorderedList")}><List size={16} /></button>
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="編號清單" type="button" onClick={() => command("insertOrderedList")}><ListOrdered size={16} /></button>
        <button className="inline-flex h-9 items-center justify-center rounded-md px-2 text-xs font-bold text-[#315746] hover:bg-[#e9f4ec]" title="增加縮排" type="button" onClick={() => command("indent")}>縮排</button>
        <button className="inline-flex h-9 items-center justify-center rounded-md px-2 text-xs font-bold text-[#315746] hover:bg-[#e9f4ec]" title="減少縮排" type="button" onClick={() => command("outdent")}>凸排</button>
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="引言" type="button" onClick={() => command("formatBlock", "blockquote")}><Quote size={16} /></button>
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="插入分隔線" type="button" onClick={() => insertHtml("<hr />")}><Minus size={16} /></button>
        <span className="mx-1 h-6 w-px bg-[#dfe6e1]" aria-hidden="true" />
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="靠左" type="button" onClick={() => command("justifyLeft")}><AlignLeft size={16} /></button>
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="置中" type="button" onClick={() => command("justifyCenter")}><AlignCenter size={16} /></button>
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="靠右" type="button" onClick={() => command("justifyRight")}><AlignRight size={16} /></button>
        <span className="mx-1 h-6 w-px bg-[#dfe6e1]" aria-hidden="true" />
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="插入連結" type="button" onClick={() => openInsertPanel("link")}><Link size={16} /></button>
        <button className="inline-flex h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-bold text-[#315746] hover:bg-[#e9f4ec]" title="插入圖片網址" type="button" onClick={() => openInsertPanel("image")}><ImagePlus size={16} />圖片網址</button>
        <button className="inline-flex h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-bold text-[#315746] hover:bg-[#e9f4ec] disabled:opacity-50" disabled={editorUploading} title="上傳圖片" type="button" onClick={() => imageInputRef.current?.click()}><Upload size={16} />上傳圖片</button>
        <input ref={imageInputRef} accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" type="file" onChange={(event) => uploadEditorImage(event.target.files)} />
        <button className="inline-flex h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-bold text-[#315746] hover:bg-[#e9f4ec]" title="插入 YouTube" type="button" onClick={() => openInsertPanel("video")}><Video size={16} />YouTube</button>
        <button className="inline-flex h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-bold text-[#315746] hover:bg-[#e9f4ec]" title="嵌入其他網站內容（地圖、表單、Vimeo、Spotify 等）" type="button" onClick={() => openInsertPanel("embed")}><Frame size={16} />嵌入</button>
        <button className="inline-flex h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-bold text-[#315746] hover:bg-[#e9f4ec]" title="插入表格" type="button" onClick={() => openInsertPanel("table")}><TableIcon size={16} />表格</button>
        <span className="mx-1 h-6 w-px bg-[#dfe6e1]" aria-hidden="true" />
        <ColorSwatchButton icon={<Palette size={16} />} title="字體顏色" value={foreColor} onMouseDown={saveSelection} onPick={applyForeColor} />
        <ColorSwatchButton icon={<Highlighter size={16} />} title="螢光標色" value={highlightColor} onMouseDown={saveSelection} onPick={applyHighlightColor} />
        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#315746] hover:bg-[#e9f4ec]" title="清除格式" type="button" onClick={() => command("removeFormat")}><Eraser size={16} /></button>
        <label className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm font-bold text-[#315746] hover:bg-[#e9f4ec]" title="字體大小" onMouseDown={(event) => event.stopPropagation()}>
          <Type size={16} />
          <select key={`fontsize-${selectResetKey}`} className="bg-transparent outline-none" defaultValue="" onMouseDown={saveSelection} onChange={(event) => { if (event.target.value) { command("fontSize", event.target.value); setSelectResetKey((current) => current + 1); } }}>
            <option value="" disabled>大小</option>
            <option value="2">小</option>
            <option value="3">一般</option>
            <option value="4">大</option>
            <option value="5">特大</option>
          </select>
        </label>
        <label className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm font-bold text-[#315746] hover:bg-[#e9f4ec]" title="段落標題" onMouseDown={(event) => event.stopPropagation()}>
          <select key={`format-${selectResetKey}`} className="bg-transparent outline-none" defaultValue="" onMouseDown={saveSelection} onChange={(event) => { if (event.target.value) { command("formatBlock", event.target.value); setSelectResetKey((current) => current + 1); } }}>
            <option value="" disabled>段落</option>
            <option value="p">一般段落</option>
            <option value="h1">H1 標題</option>
            <option value="h2">H2 標題</option>
            <option value="h3">H3 標題</option>
            <option value="h4">H4 標題</option>
            <option value="h5">H5 標題</option>
            <option value="h6">H6 標題</option>
          </select>
        </label>
        <span className="mx-1 h-6 w-px bg-[#dfe6e1]" aria-hidden="true" />
        <button
          className="ml-auto inline-flex h-9 items-center justify-center gap-2 rounded-md px-3 text-sm font-bold text-[#315746] hover:bg-[#e9f4ec]"
          title={isFullscreen ? "退出全螢幕（Esc）" : "全螢幕編輯"}
          type="button"
          onClick={() => setIsFullscreen((current) => !current)}
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          {isFullscreen ? "退出全螢幕" : "全螢幕"}
        </button>
      </div>

      {insertPanel === "table" && (
        <div className="grid shrink-0 gap-3 border-b border-[#edf0ee] bg-white p-3 sm:grid-cols-[100px_100px_auto_auto] sm:items-end">
          <label className="grid gap-1 text-sm font-bold text-[#315746]">
            欄數
            <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" max={10} min={1} type="number" value={tableColumns} onChange={(event) => setTableColumns(event.target.value)} onKeyDown={submitTableInsertOnEnter} />
          </label>
          <label className="grid gap-1 text-sm font-bold text-[#315746]">
            列數
            <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" max={20} min={1} type="number" value={tableRows} onChange={(event) => setTableRows(event.target.value)} onKeyDown={submitTableInsertOnEnter} />
          </label>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" type="button" onClick={submitTableInsert}><Check size={16} />插入表格</button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => setInsertPanel(null)}><X size={16} />取消</button>
        </div>
      )}

      {insertPanel && insertPanel !== "table" && (
        <div className="grid shrink-0 gap-3 border-b border-[#edf0ee] bg-white p-3 md:grid-cols-[1fr_auto_auto] md:items-end">
          <label className="grid gap-1 text-sm font-bold text-[#315746]">
            {insertPanel === "image" ? "圖片網址" : insertPanel === "video" ? "YouTube 影音網址" : insertPanel === "embed" ? "嵌入網址或 <iframe> 程式碼" : "連結網址"}
            <input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder={insertPanel === "image" ? "https://example.com/image.jpg" : insertPanel === "video" ? "https://www.youtube.com/watch?v=..." : insertPanel === "embed" ? "貼上 Google 地圖／表單、Vimeo、Spotify 的嵌入網址或程式碼" : "https://example.com"} value={urlValue} onChange={(event) => { setUrlValue(event.target.value); setInsertError(""); }} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); submitUrlInsert(); } }} />
            {insertError ? (
              <span className="mt-1 block text-xs font-normal text-[#c3423f]">{insertError}</span>
            ) : insertPanel === "embed" ? (
              <span className="mt-1 block text-xs font-normal text-[#9a8a7a]">支援 Google 地圖、Google 表單、Vimeo、Spotify 提供的「嵌入」網址或程式碼，其他來源網址基於安全考量無法嵌入。</span>
            ) : null}
          </label>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" type="button" onClick={submitUrlInsert}><Check size={16} />插入</button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={() => { setInsertPanel(null); setInsertError(""); }}><X size={16} />取消</button>
        </div>
      )}
      </div>

      {imageToolbar && (
        <div
          className="absolute z-20 w-[min(360px,calc(100%-24px))] rounded-md border border-[#cfd8d2] bg-white p-3 shadow-[0_14px_40px_rgba(32,61,50,.18)]"
          style={{ left: imageToolbar.left, top: imageToolbar.top }}
          onMouseDown={imageToolbarMouseDown}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-bold text-[#59665f]">圖片設定</span>
            <button className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[#59665f] hover:bg-[#f0f5f2]" type="button" onClick={() => selectImage(null)} title="關閉圖片工具">
              <X size={15} />
            </button>
          </div>
          <div className="mb-2 grid grid-cols-4 gap-1.5">
            {["25", "50", "75", "100"].map((width) => (
              <button className={imageButtonClass(selectedImageWidth === width)} key={width} type="button" onClick={() => resizeSelectedImage(width)}>
                {width}%
              </button>
            ))}
          </div>
          <div className="mb-3 grid grid-cols-3 gap-1.5">
            <button className={imageButtonClass(selectedImageAlign === "left")} type="button" onClick={() => alignSelectedImage("left")}><AlignLeft size={15} />靠左</button>
            <button className={imageButtonClass(selectedImageAlign === "center")} type="button" onClick={() => alignSelectedImage("center")}><AlignCenter size={15} />置中</button>
            <button className={imageButtonClass(selectedImageAlign === "right")} type="button" onClick={() => alignSelectedImage("right")}><AlignRight size={15} />靠右</button>
          </div>
          <div className="grid grid-cols-[1fr_56px] items-center gap-2">
            <input className="accent-[#315746]" max={100} min={20} type="range" value={selectedImageWidth} onChange={(event) => resizeSelectedImage(event.target.value)} />
            <span className="rounded-md border border-[#dfe6e1] bg-white px-2 py-1.5 text-center text-xs font-bold text-[#315746]">{selectedImageWidth}%</span>
          </div>
          <div className="mt-3 grid gap-2">
            <label className="grid gap-1 text-xs font-bold text-[#59665f]" onMouseDown={(event) => event.stopPropagation()}>
              Alt
              <input
                className="h-9 rounded-md border border-[#cfd8d2] px-2 text-sm font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                value={selectedImageAlt}
                onChange={(event) => updateSelectedImageAlt(event.target.value)}
              />
            </label>
            <label className="grid gap-1 text-xs font-bold text-[#59665f]" onMouseDown={(event) => event.stopPropagation()}>
              圖片說明
              <input
                className="h-9 rounded-md border border-[#cfd8d2] px-2 text-sm font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                value={selectedImageCaption}
                onChange={(event) => updateSelectedImageCaption(event.target.value)}
                placeholder="可留空"
              />
            </label>
            <label className="grid gap-1 text-xs font-bold text-[#59665f]" onMouseDown={(event) => event.stopPropagation()}>
              圖片連結
              <input
                className="h-9 rounded-md border border-[#cfd8d2] px-2 text-sm font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                value={selectedImageLink}
                onChange={(event) => updateSelectedImageLink(event.target.value)}
                placeholder="https:// 或留空"
              />
            </label>
            <button className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-[#f2b8b5] bg-white px-2.5 text-xs font-bold text-[#b3261e] transition hover:bg-[#fdebea]" type="button" onClick={deleteSelectedImage}>
              <Trash2 size={14} />刪除圖片
            </button>
          </div>
        </div>
      )}

      {tableToolbar && (
        <div
          className="absolute z-20 w-[min(460px,calc(100%-24px))] rounded-md border border-[#cfd8d2] bg-white p-3 shadow-[0_14px_40px_rgba(32,61,50,.18)]"
          style={{ left: tableToolbar.left, top: tableToolbar.top }}
          onMouseDown={imageToolbarMouseDown}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-bold text-[#59665f]">表格設定</span>
            <button className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[#59665f] hover:bg-[#f0f5f2]" type="button" onClick={() => selectCell(null)} title="關閉表格工具">
              <X size={15} />
            </button>
          </div>
          <div className="mb-2 grid grid-cols-4 gap-1.5">
            {["50", "75", "100"].map((width) => (
              <button className={imageButtonClass(tableWidth === width)} key={width} type="button" onClick={() => resizeSelectedTable(width)}>
                {width}%
              </button>
            ))}
            <button className={imageButtonClass(false)} type="button" onClick={toggleHeaderRow}>標題列</button>
          </div>
          <div className="mb-2 grid grid-cols-3 gap-1.5">
            <button className={imageButtonClass(tableAlign === "left")} type="button" onClick={() => alignSelectedTable("left")}><AlignLeft size={15} />靠左</button>
            <button className={imageButtonClass(tableAlign === "center")} type="button" onClick={() => alignSelectedTable("center")}><AlignCenter size={15} />置中</button>
            <button className={imageButtonClass(tableAlign === "right")} type="button" onClick={() => alignSelectedTable("right")}><AlignRight size={15} />靠右</button>
          </div>
          <div className="mb-2 grid gap-2 sm:grid-cols-2">
            <label className="grid gap-1 text-xs font-bold text-[#59665f]" onMouseDown={(event) => event.stopPropagation()}>
              邊框
              <select
                className="h-9 rounded-md border border-[#cfd8d2] px-2 text-sm font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                value={tableBorder}
                onChange={(event) => applyTableBorder(event.target.value as TableBorder)}
              >
                <option value="none">無邊框</option>
                <option value="thin">細線</option>
                <option value="thick">粗線</option>
                <option value="dashed">虛線</option>
              </select>
            </label>
            <label className="grid gap-1 text-xs font-bold text-[#59665f]" onMouseDown={(event) => event.stopPropagation()}>
              內距
              <select
                className="h-9 rounded-md border border-[#cfd8d2] px-2 text-sm font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                value={tablePadding}
                onChange={(event) => applyTablePadding(event.target.value as CellPadding)}
              >
                <option value="small">小</option>
                <option value="medium">中</option>
                <option value="large">大</option>
              </select>
            </label>
          </div>
          <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_auto]">
            <div className="grid grid-cols-3 gap-1.5">
              <button className={imageButtonClass(tableCellAlign === "left")} type="button" onClick={() => applySelectedCellTextAlign("left")}><AlignLeft size={15} />單格左</button>
              <button className={imageButtonClass(tableCellAlign === "center")} type="button" onClick={() => applySelectedCellTextAlign("center")}><AlignCenter size={15} />單格中</button>
              <button className={imageButtonClass(tableCellAlign === "right")} type="button" onClick={() => applySelectedCellTextAlign("right")}><AlignRight size={15} />單格右</button>
            </div>
            <label className="relative inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-[#cfd8d2] bg-white px-2.5 text-xs font-bold text-[#315746] hover:bg-[#f0f5f2]" title="單格背景色" onMouseDown={(event) => event.stopPropagation()}>
              <Palette size={14} />
              <span className="h-4 w-4 rounded border border-[#cfd8d2]" style={{ backgroundColor: tableCellBg }} />
              <input className="absolute inset-0 h-full w-full cursor-pointer opacity-0" type="color" value={tableCellBg} onChange={(event) => applySelectedCellBackground(event.target.value)} />
            </label>
          </div>
          <div className="mb-2 grid grid-cols-2 gap-1.5">
            <button className={imageButtonClass(false)} type="button" onClick={() => insertTableRow("above")}><Rows3 size={15} />上方插入列</button>
            <button className={imageButtonClass(false)} type="button" onClick={() => insertTableRow("below")}><Rows3 size={15} />下方插入列</button>
            <button className={imageButtonClass(false)} type="button" onClick={() => insertTableColumn("left")}><Columns3 size={15} />左方插入欄</button>
            <button className={imageButtonClass(false)} type="button" onClick={() => insertTableColumn("right")}><Columns3 size={15} />右方插入欄</button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <button className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-[#f2b8b5] bg-white px-2.5 text-xs font-bold text-[#b3261e] transition hover:bg-[#fdebea]" type="button" onClick={deleteTableRow}><Trash2 size={14} />刪除此列</button>
            <button className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-[#f2b8b5] bg-white px-2.5 text-xs font-bold text-[#b3261e] transition hover:bg-[#fdebea]" type="button" onClick={deleteTableColumn}><Trash2 size={14} />刪除此欄</button>
          </div>
          <button className="mt-1.5 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-[#f2b8b5] bg-white px-2.5 text-xs font-bold text-[#b3261e] transition hover:bg-[#fdebea]" type="button" onClick={deleteTable}><Trash2 size={14} />刪除整個表格</button>
        </div>
      )}

      <div
        className={`overflow-auto resize-y px-4 py-3 text-sm leading-7 text-[#34443b] outline-none focus:ring-2 focus:ring-[#315746]/15 [&_a]:font-bold [&_a]:text-[#245438] [&_blockquote]:border-l-4 [&_blockquote]:border-[#d7c7b6] [&_blockquote]:pl-4 [&_font[size='2']]:text-sm [&_font[size='4']]:text-lg [&_font[size='5']]:text-xl [&_h1]:mb-4 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-bold [&_h4]:mb-2 [&_h4]:text-base [&_h4]:font-bold [&_h5]:mb-2 [&_h5]:text-sm [&_h6]:mb-2 [&_h6]:text-xs [&_iframe]:my-4 [&_iframe]:aspect-video [&_iframe]:h-auto [&_iframe]:w-full [&_iframe]:rounded-md [&_iframe]:border-0 [&_img]:my-4 [&_img]:max-h-[520px] [&_img]:max-w-full [&_img]:cursor-pointer [&_img]:rounded-md [&_img]:object-contain [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-3 [&_strong]:font-bold [&_table]:max-w-full [&_ul]:list-disc ${isFullscreen ? "flex-1 resize-none" : "min-h-[260px] max-h-none"}`}
        contentEditable
        ref={editorRef}
        role="textbox"
        suppressContentEditableWarning
        onBlur={() => emitChange(true)}
        onClick={(event) => {
          const target = event.target;
          const image = target instanceof HTMLImageElement ? target : null;
          selectImage(image);
          if (!image) selectCell(null);
          saveSelection();
        }}
        onContextMenu={(event) => {
          const target = event.target;
          const cell = target instanceof HTMLElement ? target.closest("td") : null;
          if (!cell || !editorRef.current?.contains(cell)) return;
          event.preventDefault();
          selectImage(null);
          selectCell(cell);
          saveSelection();
        }}
        onInput={() => {
          saveSelection();
          emitChange();
        }}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onPaste={handlePaste}
      />
      {editorUploading && <div className="shrink-0 border-t border-[#edf0ee] px-4 py-2 text-sm font-medium text-[#59665f]">圖片上傳中...</div>}
      </div>
    </div>
  );

  return (
    <>
      <div ref={placeholderRef} className="contents" />
      {createPortal(editorBody, portalContainer)}
    </>
  );
}
